import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Loader from './Loader';



const TaskReviewModal = ({ 
  isOpen, 
  onClose, 
  task, 
  memberName,
  memberId,
  onSubmit, 
  isDarkMode = false 
}) => {
  const taskId = task?.id || task?._id;
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewLink, setReviewLink] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Clear fields when modal closes
  useEffect(() => {
    if (!isOpen) {
      setReviewNotes('');
      setReviewLink('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reviewNotes.trim()) {
      alert('Please enter review notes');
      return;
    }

    setIsSubmitting(true);

    try {
      //setIsSubmitting(false);
      const accessToken = localStorage.getItem('accessToken');
      
      const requestBody = {
        attachmentsLink: reviewLink || "#",
        reviewNotes: reviewNotes,
      };
      
      const response = await fetch(
        `https://taskscheduler-backend.onrender.com/api/v1/task/member/${taskId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );
      
      const result = await response.json();
      if (response.ok) {
        // Success toast
        alert('Review submitted successfully! Task will be marked as completed.');
        
        // Refresh task list
        if (onSubmit) {
          onSubmit();
        }
        
        onClose();
      } else {
        alert(`Failed to submit review: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        animation: 'fadeIn 0.2s ease-out',
        overflow: 'auto'
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)'
        }}
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div 
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '32rem',
          backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
          color: isDarkMode ? '#ffffff' : '#111827',
          borderRadius: '1rem',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: 'scaleIn 0.2s ease-out'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <h2 
                id="modal-title"
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  marginBottom: '0.25rem',
                  margin: 0
                }}
              >
                Submit Task Review
              </h2>
              <p style={{
                fontSize: '0.875rem',
                color: isDarkMode ? '#9ca3af' : '#6b7280',
                margin: 0
              }}>
                Submit your completed work for admin review
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '0.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                background: 'transparent',
                color: isDarkMode ? '#9ca3af' : '#6b7280',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                opacity: isSubmitting ? 0.5 : 1
              }}
              onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6')}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              aria-label="Close modal"
            >
              <X style={{ width: 20, height: 20 }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.25rem 1.5rem'
        }}>
          {/* Task Info */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            borderRadius: '0.5rem',
            backgroundColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : '#f9fafb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{
                fontSize: '0.875rem',
                fontWeight: '500',
                color: isDarkMode ? '#d1d5db' : '#6b7280'
              }}>
                Task
              </span>
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '500',
                backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : '#dbeafe',
                color: isDarkMode ? '#93c5fd' : '#1e40af'
              }}>
                {task?.status || 'pending'}
              </span>
            </div>
            <p style={{ margin: 0, fontWeight: '500' }}>
              {task?.taskName || task?.title || 'Untitled Task'}
            </p>
          </div>

          {/* Review Link Input */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label 
              htmlFor="review-link"
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                marginBottom: '0.5rem',
                color: isDarkMode ? '#d1d5db' : '#374151'
              }}
            >
              Review Link (Optional)
            </label>
            <input
              type="url"
              id="review-link"
              value={reviewLink}
              onChange={(e) => setReviewLink(e.target.value)}
              placeholder="https://github.com/... or https://drive.google.com/..."
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                backgroundColor: isDarkMode ? '#374151' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#111827',
                outline: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                boxSizing: 'border-box',
                fontSize: '0.875rem',
                opacity: isSubmitting ? 0.6 : 1
              }}
              onFocus={(e) => {
                if (!isSubmitting) {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? '#4b5563' : '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
            <p style={{
              fontSize: '0.75rem',
              color: isDarkMode ? '#9ca3af' : '#6b7280',
              marginTop: '0.5rem',
              marginBottom: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem'
            }}>
              <i className="fas fa-link" style={{ fontSize: '0.75rem' }}></i>
              GitHub, Google Drive, or any project link
            </p>
          </div>

          {/* Review Notes Textarea */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label 
              htmlFor="review-notes"
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                marginBottom: '0.5rem',
                color: isDarkMode ? '#d1d5db' : '#374151'
              }}
            >
              Review Notes <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <textarea
              id="review-notes"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Describe what you've completed, challenges faced, and any important notes for the admin..."
              rows={6}
              required
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                backgroundColor: isDarkMode ? '#374151' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#111827',
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                boxSizing: 'border-box',
                opacity: isSubmitting ? 0.6 : 1
              }}
              onFocus={(e) => {
                if (!isSubmitting) {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? '#4b5563' : '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Info Box */}
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff',
            border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe'}`,
            marginBottom: '1rem'
          }}>
            <p style={{
              fontSize: '0.75rem',
              color: isDarkMode ? '#93c5fd' : '#1e40af',
              margin: 0,
              display: 'flex',
              alignItems: 'start',
              gap: '0.5rem'
            }}>
              <i className="fas fa-info-circle" style={{ fontSize: '0.875rem', marginTop: '0.125rem' }}></i>
              <span>
                Admin will be notified via email. Once reviewed, your task status will be automatically updated to Completed.
              </span>
            </p>
          </div>

          {/* Sender Info */}
          <div style={{
            fontSize: '0.75rem',
            color: isDarkMode ? '#9ca3af' : '#6b7280'
          }}>
            Submitting as: <span style={{ fontWeight: '500' }}>{memberName}</span>
          </div>
        </form>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
          display: 'flex',
          gap: '0.75rem'
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '0.625rem 1rem',
              borderRadius: '0.5rem',
              fontWeight: '500',
              border: 'none',
              backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
              color: isDarkMode ? '#ffffff' : '#374151',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              opacity: isSubmitting ? 0.5 : 1
            }}
            onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = isDarkMode ? '#4b5563' : '#e5e7eb')}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6'}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '0.625rem 1rem',
              borderRadius: '0.5rem',
              fontWeight: '500',
              border: 'none',
              backgroundColor: isSubmitting ? '#9ca3af' : '#2563eb',
              color: '#ffffff',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = '#1d4ed8')}
            onMouseLeave={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = '#2563eb')}
          >
            {isSubmitting ? (
              <Loader variant="inline" size="sm" text="Submitting..." isDarkMode={isDarkMode} />
            ) : (
              <>
                <i className="fas fa-paper-plane"></i>
                Send Review
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.95);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TaskReviewModal;
